import {
  CustomMappingAssociatedValueType,
  CustomMappingOption,
  CustomMappingType,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { CustomMapping } from '../custom-mapping';
import { BaseSourceEntity } from '../source/base-source-entity';
import type {
  ITreatmentCategory,
  ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  multiSortBy$,
  nameSorter,
  query$,
  snapshot,
  undeletedQuery,
  XSLXImporterExporter,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { IBaseAppointmentCategory } from '../interfaces';
import { compact, first, sortBy, uniqBy } from 'lodash';
import { Brand, toINamedDocuments } from '@principle-theorem/principle-core';
import { TranslationMapHandler } from '../translation-map';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import Fuse from 'fuse.js';
import { XSLXToAppointmentCategoryToTreatmentCategory } from './xlsx-to-appointment-category-treatment-category';
import { Observable, from, of } from 'rxjs';
import {
  AppointmentCategoryResourceType,
  AppointmentToTreatmentCategoryToXSLX,
} from './appointment-to-treatment-category-to-xlsx';
import { PracticeMigration } from '../practice-migrations';

export const APPOINTMENT_CATEGORY_TO_TREATMENT_CATEGORY_MAPPING_TYPE =
  'appointmentCategoryToTreatmentCategory';

export const APPOINTMENT_CATEGORY_TO_TREATMENT_CATEGORY_MAPPING =
  CustomMapping.init({
    metadata: {
      label: 'Appointment Category to Treatment Category',
      description: `
        Used to map appointment categories in Exact to treatment categories/tags in Principle.
      `,
      type: APPOINTMENT_CATEGORY_TO_TREATMENT_CATEGORY_MAPPING_TYPE,
    },
    type: CustomMappingType.SelectionList,
    labelOverrides: {
      sourceIdentifier: 'Id',
      sourceLabel: 'Appointment Category',
      destinationIdentifier: 'Map To',
      associatedValue: 'Pick Category/Tag',
    },
  });

export abstract class BaseAppointmentCategoryToTreatmentCategoryMappingHandler<
  AppointmentCategory extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<AppointmentCategory>,
> extends BaseCustomMappingHandler<
  ITreatmentCategory | ITag,
  AppointmentCategoryResourceType,
  INamedDocument<ITreatmentCategory | ITag>
> {
  customMapping = APPOINTMENT_CATEGORY_TO_TREATMENT_CATEGORY_MAPPING;
  abstract sourceEntity: SourceEntity;

  abstract translateFn: (
    record: AppointmentCategory['data']
  ) => IBaseAppointmentCategory;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const category = this.translateFn(record);
        return {
          label: category.name,
          value: category.id.toString(),
        };
      });
    return sortBy(options, 'label');
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      of([
        {
          value: AppointmentCategoryResourceType.TreatmentCategory,
          description: 'Map this to a Treatment Category',
          hasAssociatedValue: true,
          associatedValueType: CustomMappingAssociatedValueType.NamedDocument,
          associatedValueDescription:
            'Which Treatment Category should be used for this?',
        },
        {
          value: AppointmentCategoryResourceType.AppointmentTag,
          description: 'Map this to an Appointment Tag',
          hasAssociatedValue: true,
          associatedValueType: CustomMappingAssociatedValueType.NamedDocument,
          associatedValueDescription:
            'Which Appointment Tag should be used for this?',
        },
      ])
    );
  }

  getAssociatedValueOptions$(
    migration: WithRef<IPracticeMigration>,
    destinationValue: { value: AppointmentCategoryResourceType }
  ): Observable<INamedDocument<ITreatmentCategory | ITag>[]> {
    if (
      destinationValue.value ===
      AppointmentCategoryResourceType.TreatmentCategory
    ) {
      return from(this.getTreatmentCategories(migration));
    }
    return from(this.getAppointmentTags(migration));
  }

  async getTreatmentCategories(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITreatmentCategory>[]> {
    return snapshot(
      query$(
        undeletedQuery(
          Brand.treatmentCategoryCol(migration.configuration.brand)
        )
      ).pipe(toINamedDocuments(), multiSortBy$(nameSorter()))
    );
  }

  async getAppointmentTags(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITag>[]> {
    return snapshot(
      query$(
        undeletedQuery(Brand.appointmentTagCol(migration.configuration.brand))
      ).pipe(toINamedDocuments(), multiSortBy$(nameSorter()))
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const records = await this.getRecords(translationMap);
    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = sourceOptions.filter(
      (option) => !mappedItemCodes.includes(option.value)
    );

    const treatmentCategories = await this.getTreatmentCategories(migration);
    const appointmentTags = await this.getAppointmentTags(migration);

    const destinationOptions = [
      ...treatmentCategories.map((category) => ({
        name: category.name,
        associatedValue: category,
        destinationValue: AppointmentCategoryResourceType.TreatmentCategory,
      })),
      ...appointmentTags.map((tag) => ({
        name: tag.name,
        associatedValue: tag,
        destinationValue: AppointmentCategoryResourceType.AppointmentTag,
      })),
    ];

    const recordsForUpload = unmappedItemCodes.map((item) => {
      const fuse = new Fuse(destinationOptions, {
        keys: ['name'],
        includeScore: true,
        threshold: 0.3,
      });
      const matches = fuse.search(item.label);
      const match = first(matches)?.item;

      return match
        ? {
            associatedValue: match.associatedValue,
            destinationValue: { value: match.destinationValue },
            sourceIdentifier: item.value,
            sourceLabel: item.label,
          }
        : undefined;
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, appointmentCategories, translator } =
      await this._getExporterData(migration);
    await new XSLXImporterExporter().download(
      fileName,
      appointmentCategories,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: Blob
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToAppointmentCategoryToTreatmentCategory()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const treatmentCategories = await this.getTreatmentCategories(migration);
    const appointmentTags = await this.getAppointmentTags(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const records = items.map((item) => {
      const mapTo = item.mapTo;
      const matchingOption = sourceOptions.find(
        (option) => option.label === item.label
      );
      if (!matchingOption) {
        return;
      }

      const foundCategory = treatmentCategories.find(
        (category) => category.name === mapTo
      );
      if (foundCategory) {
        return {
          associatedValue: foundCategory,
          destinationValue: {
            value: AppointmentCategoryResourceType.TreatmentCategory,
          },
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
        };
      }

      const foundTag = appointmentTags.find((tag) => tag.name === mapTo);
      if (mapTo?.trim() && !foundTag) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find destination for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        associatedValue: foundTag,
        destinationValue: {
          value: AppointmentCategoryResourceType.AppointmentTag,
        },
        sourceIdentifier: matchingOption.value,
        sourceLabel: matchingOption.label,
      };
    });

    await this.upsertBulk(compact(records), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, appointmentCategories, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      appointmentCategories,
      translator
    );
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    appointmentCategories: IBaseAppointmentCategory[];
    translator: AppointmentToTreatmentCategoryToXSLX;
  }> {
    const fileName = this.getFileName();
    const appointmentCategories = await this._getSourceCategories(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new AppointmentToTreatmentCategoryToXSLX(
      await this.getRecords(translationMap),
      await this.getTreatmentCategories(migration),
      await this.getAppointmentTags(migration)
    );
    return { fileName, appointmentCategories, translator };
  }

  private async _getSourceCategories(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseAppointmentCategory[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      uniqBy(
        records.map((record) => this.translateFn(record.data)),
        (category) => category.id
      ),
      'name'
    );
  }
}
